import { ReactNode, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useSession } from "@/auth/session/SessionProvider";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { LandingPage } from "@/features/landing/LandingPage";
import { PublicChatRoute } from "@/features/marketplace/PublicChatRoute";
import { marketplaceIsOffered } from "@/features/marketplace/providers";

/**
 * Gate component. Renders children only after the user is authenticated.
 *
 * <p><b>The app surface (scheda.delgentic.com) shows the Scheda product landing
 * to logged-out visitors.</b> This reverses an earlier "sign-in-or-app, never
 * marketing" rule: that rule dated from when {@code /signin} was bare and ALL
 * marketing lived on the apex (delgentic.com, the Delgentic-company landing).
 * Scheda now has its own product landing ({@link LandingPage}) under the wider
 * Delgentic brand, so it belongs here. The two-clicks concern the old rule named
 * is avoided by keeping sign-in a MODAL over the landing ({@code AuthModal}) — and
 * auto-opening it when the visitor arrived to sign in (a gated deep link), so
 * that path stays one click. (Follow-up: the apex swaps its Sign-in button for a
 * "Go to Scheda" CTA, so auth happens here.)</p>
 *
 * <p><b>Where "sign in" goes depends on the mode</b> (see {@code auth/cognito/pool.ts}):</p>
 * <ul>
 *   <li><b>cognito</b> — the {@link LandingPage} with its in-modal sign-in, on our
 *       own origin. No hop to another origin at all, and the visitor's deep link is
 *       carried into the modal so signing in lands them where they were headed.</li>
 *   <li><b>oidc</b> — the redirect to the IdP, as before. Local dev (Keycloak)
 *       and the production rollback both take this path. The hop through this
 *       origin is structural there: PKCE requires the origin that STARTS the
 *       flow to be the one that COMPLETES it, because the code_verifier and
 *       state live in this origin's localStorage.</li>
 * </ul>
 *
 * <p>Public routes (/@handle, /privacy, /cookies, /book/*, and the sign-in
 * pages themselves) are matched in App.tsx BEFORE this gate, so they stay
 * anonymously reachable and are not swept into sign-in.</p>
 */
const CHAT_PATH = "/chat";

export function AuthGate({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const { t } = useTranslation();

  // Fire the redirect at most once per mount. Without the guard the effect
  // re-runs while the redirect is still in flight and stacks up navigations.
  // Only the oidc path needs this — the cognito path is a plain <Navigate>.
  const redirectStarted = useRef(false);

  const needsRedirectSignIn =
    session.mode === "oidc" && !session.isLoading && !session.isAuthenticated && !session.error;

  useEffect(() => {
    if (needsRedirectSignIn && !redirectStarted.current) {
      redirectStarted.current = true;
      session.signIn();
    }
  }, [needsRedirectSignIn, session]);

  if (session.error) {
    return (
      <div className="grid min-h-screen place-items-center bg-background p-6">
        <LanguageSwitcher floating />
        <div className="comic-shadow max-w-sm rounded-xl border border-border bg-card p-8 text-center">
          <h2 className="font-display text-lg font-semibold text-destructive">
            {t("auth.failedTitle")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">{session.error.message}</p>
          <button
            type="button"
            onClick={() => session.signIn()}
            className="mt-5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
          >
            {t("auth.retry")}
          </button>
        </div>
      </div>
    );
  }

  if (!session.isAuthenticated) {
    // Native mode: show the Scheda landing (its own metallic sign-in modal handles
    // auth). If they were headed somewhere specific (a gated deep link), open the
    // modal straight away and carry that path so sign-in lands them there — so it's
    // still one click, without losing the marketing page for direct visitors.
    if (session.mode === "cognito" && !session.isLoading) {
      // The A2A chat is the one page an outsider is meant to see logged out, so it forks here
      // rather than above this gate (D-57). Here is the only place that already distinguishes
      // "logged out" from "still finding out": branching higher would need its own session
      // check, and the only provider-free signal is the token store, which this tree populates
      // and is therefore null on first paint. A member landing on /chat cold would watch the
      // signed-out view flash before their own.
      //
      // No auto-opened sign-in modal either: the page's own sign-in choices are the point, and
      // they are three identity providers rather than a Scheda account (D-4).
      // Only where there is a way in. A deployment that brokers no identity provider has no
      // signed-out chat to offer, and showing an empty sign-in panel would be a page whose whole
      // purpose is a set of buttons that are not there (D-85). Falls through to the landing page,
      // which is what this route did before the marketplace existed.
      if (location.pathname === CHAT_PATH && marketplaceIsOffered()) {
        // Which of the two public views is decided from a cookie, not a call: pre-verification
        // this route is the bundle and nothing else (D-37, D-77).
        return <PublicChatRoute />;
      }
      const params = new URLSearchParams(location.search);
      params.delete("auth"); // the modal's own param — never part of the post-login destination
      const qs = params.toString();
      const target = location.pathname + (qs ? `?${qs}` : "");
      return <LandingPage from={target} autoOpenAuth={location.pathname !== "/"} />;
    }
    // Covers the initial session check AND the moment before a redirect leaves.
    // "Signing you in…" is only honest while exchanging a code; otherwise we're
    // on our way out to the IdP.
    const returningFromIdp =
      /[?&]code=/.test(window.location.search) &&
      /[?&]state=/.test(window.location.search);
    return (
      <div className="grid min-h-screen place-items-center bg-background text-sm text-muted-foreground">
        <LanguageSwitcher floating />
        <p>{returningFromIdp ? t("auth.signingIn") : t("auth.takingToSignin")}</p>
      </div>
    );
  }

  return <>{children}</>;
}
